import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Download, Receipt, Users, Package, Coins } from "lucide-react";
import {
  COUNTRIES,
  formatPrice,
  type Country,
  type PurchasesAdminQuery,
} from "@nh/shared";
import { PageHeader } from "@/shared/ui/layout";
import { Alert, Button, Input, Select } from "@/shared/ui/core";
import { EmptyState, SkeletonTable } from "@/shared/ui/ui";
import {
  AdminGuard,
  DataTable,
  DataTableFrame,
  FilterBar,
  KpiCard,
  Pagination,
  Td,
  Th,
  Tr,
  DEFAULT_PER_PAGE,
} from "@/features/admin/components";
import { downloadPurchasesCsv } from "@/features/admin/orders/orders.api";
import {
  useAdminPurchases,
  usePurchasesSummary,
} from "@/features/admin/orders/orders.queries";
import { formatOrderDate } from "@/features/admin/orders/format";


/** Primer día del mes actual y hoy: el corte que contabilidad pide. */
function currentMonthRange(): { from: string; to: string } {
  const now = new Date();
  const from = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
  return { from: from.toISOString().slice(0, 10), to: now.toISOString().slice(0, 10) };
}

const COUNTRY_LABELS: Record<string, string> = {
  MX: "México",
  US: "Estados Unidos",
  OTHER: "Otro",
  "—": "Sin país",
};

/**
 * ═══════════════════════════════════════════════════════════════════
 * Compras — el dashboard financiero (Anahí, 27-ago).
 *
 * Para el equipo de finanzas y contabilidad: filtro por periodo y por
 * país, los totales del mes (ingresos POR MONEDA — jamás sumadas entre
 * sí—, compras, unidades, clientes, puntos) y la descarga en CSV que
 * Excel abre con doble clic. Las devoluciones no cuentan como ingreso.
 *
 * Rediseño del 27-ago, dictado de Anahí sobre capturas: SIN gráfica
 * diaria (estorbaba), los KPIs cubren TODO el ancho, en los filtros el
 * label va EN LA MISMA FILA que su input, y la tabla ocupa el ancho
 * completo — el corte "Por país" que vivía al lado se fue (el filtro de
 * país y la columna País ya cuentan esa historia).
 * ═══════════════════════════════════════════════════════════════════
 */
export function AdminPurchasesPage() {
  return (
    <AdminGuard>
      <PurchasesScreen />
    </AdminGuard>
  );
}

function PurchasesScreen() {
  const { t } = useTranslation();
  const defaults = useMemo(currentMonthRange, []);
  const [from, setFrom] = useState(defaults.from);
  const [to, setTo] = useState(defaults.to);
  const [country, setCountry] = useState<string>("");
  const [page, setPage] = useState(1);
  /*
   * Las filas por página las elige quien mira (Anahí, 29-ago): 5, 10 o 20,
   * con CINCO por defecto. Vive en el estado y no como constante porque
   * ahora cambia en caliente; al cambiarlo se vuelve a la página 1, que es
   * lo único sensato — la página 4 de 5 en 5 no es la página 4 de 20 en 20.
   */
  const [perPage, setPerPage] = useState<number>(DEFAULT_PER_PAGE);
  const [exportError, setExportError] = useState(false);
  const [exporting, setExporting] = useState(false);

  const query: PurchasesAdminQuery = {
    page,
    perPage: perPage,
    ...(from ? { from } : {}),
    ...(to ? { to } : {}),
    ...(country ? { country: country as Country } : {}),
  };

  const purchases = useAdminPurchases(query);
  const summary = usePurchasesSummary({ ...query, page: 1 });

  const items = purchases.data?.items ?? [];
  const total = purchases.data?.total ?? 0;
  const totals = summary.data?.totals;

  async function exportar() {
    setExportError(false);
    setExporting(true);
    try {
      await downloadPurchasesCsv(query);
    } catch {
      setExportError(true);
    } finally {
      setExporting(false);
    }
  }

  return (
    <>
      <PageHeader
        titleHidden
        title={t("Compras")}
        description={t("El estado financiero del periodo, listo para contabilidad.")}
      />

      {/* ── Los números del periodo ───────────────────────────────────── */}
      {/*
        `auto-fit` y no un número fijo de columnas: la fila REPARTE el
        ancho completo entre las tarjetas que haya (4 hoy, 5 si un día
        entra USD), sin dejar un hueco muerto a la derecha (Anahí).

        Fuera la de "PV · CV" (Anahí, 29-ago). Esta pantalla es el estado
        FINANCIERO del periodo —lo que entró, cuántas compras, cuántas
        unidades, cuánta gente—; los puntos de volumen son la moneda del
        plan de comisiones y viven en su sección. Mezclarlos aquí ponía
        dos cifras sin signo de peso junto a cuatro que sí lo llevan, y
        de paso apretaba la de ingresos hasta cortarla.
      */}
      {totals && (
        <div className="mb-4 grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(170px,1fr))]">
          {totals.revenue.length === 0 ? (
            <KpiCard icon={Coins} label={t("Ingresos")} value="—" />
          ) : (
            totals.revenue.map((row) => (
              <KpiCard
                key={row.currency}
                icon={Coins}
                label={t("Ingresos {{currency}}", { currency: row.currency })}
                value={formatPrice(row.amountCents, row.currency)}
              />
            ))
          )}
          <KpiCard icon={Receipt} label={t("Compras")} value={String(totals.purchases)} />
          <KpiCard icon={Package} label={t("Unidades")} value={String(totals.units)} />
          <KpiCard icon={Users} label={t("Clientes")} value={String(totals.clients)} />
        </div>
      )}

      {/* ── El libro del periodo, con SUS filtros dentro de la card,
          como en Órdenes: el mismo diseño en todas las secciones. ────── */}
      <DataTableFrame aria-busy={purchases.isFetching}>
        <FilterBar
          actions={
            <Button
              variant="secondary"
              leftIcon={<Download />}
              loading={exporting}
              onClick={() => void exportar()}
            >
              {t("Descargar Excel")}
            </Button>
          }
        >
          <label className="grid gap-1 text-[12px] font-medium text-ds-ink-3">
            {t("Desde")}
            <Input
              type="date"
              value={from}
              onChange={(event) => {
                setFrom(event.target.value);
                setPage(1);
              }}
            />
          </label>
          <label className="grid gap-1 text-[12px] font-medium text-ds-ink-3">
            {t("Hasta")}
            <Input
              type="date"
              value={to}
              onChange={(event) => {
                setTo(event.target.value);
                setPage(1);
              }}
            />
          </label>
          <label className="grid gap-1 text-[12px] font-medium text-ds-ink-3">
            {t("País")}
            <div className="w-44">
              <Select
                aria-label={t("País")}
                value={country}
                onChange={(event) => {
                  setCountry(event.target.value);
                  setPage(1);
                }}
                placeholder={t("Todos")}
                options={COUNTRIES.map((code) => ({
                  value: code,
                  label: COUNTRY_LABELS[code] ?? code,
                }))}
              />
            </div>
          </label>
        </FilterBar>
        {exportError && (
          <Alert variant="error" className="m-4" onDismiss={() => setExportError(false)}>
            {t("No se pudo generar el archivo. Inténtalo otra vez.")}
          </Alert>
        )}
          {purchases.isPending ? (
            <div className="p-5">
              <SkeletonTable rows={6} columns={8} />
            </div>
          ) : items.length === 0 ? (
            <div className="p-4">
              <EmptyState
                icon={<Receipt className="h-5 w-5 text-ds-ink-3" />}
                title={t("Sin compras en este periodo")}
                description={t("Ajusta el rango de fechas o el país.")}
              />
            </div>
          ) : (
            <DataTable caption={t("Compras registradas")}>
              <thead>
                <tr>
                  <Th>{t("Folio")}</Th>
                  <Th>{t("Cliente")}</Th>
                  <Th>{t("País")}</Th>
                  <Th>{t("Producto")}</Th>
                  <Th align="right">{t("Monto")}</Th>
                  <Th align="right">{t("PV")}</Th>
                  <Th align="right">{t("Fecha")}</Th>
                </tr>
              </thead>
              <tbody>
                {items.map((purchase) => (
                  <Tr key={purchase.id}>
                    <Td className="font-mono text-[13px]">{purchase.folio}</Td>
                    <Td>
                      <p className="font-medium">{purchase.customer.name}</p>
                      <p className="text-[12px] text-ds-ink-3">{purchase.customer.email}</p>
                    </Td>
                    <Td>{COUNTRY_LABELS[purchase.country ?? "—"] ?? purchase.country}</Td>
                    <Td>
                      {purchase.productName}
                      {purchase.quantity > 1 && (
                        <span className="text-ds-ink-3"> × {purchase.quantity}</span>
                      )}
                      {purchase.status === "refunded" && (
                        <span className="ml-1 text-[12px] text-ds-terracotta">
                          · {t("Reembolsada")}
                        </span>
                      )}
                    </Td>
                    <Td align="right" className="font-medium">
                      {formatPrice(purchase.amountCents, purchase.currency)}
                    </Td>
                    <Td align="right">{purchase.volumePoints}</Td>
                    <Td align="right" className="text-ds-ink-3">
                      {formatOrderDate(purchase.createdAt)}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </DataTable>
          )}

          {total > 0 && (
            <Pagination page={page} perPage={perPage} total={total} onPageChange={setPage}
            onPerPageChange={(n) => {
              setPerPage(n);
              setPage(1);
            }}
          />
          )}
        </DataTableFrame>
    </>
  );
}
